import type { CartItem } from '../../pos/app-api';
import type { StaffTableDraftPayload, TableOrderDraftLine } from '../domain/hospitalityTypes';

function createLineId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Normaliza una línea persistida en cart_json de table_session. */
export function mapStoredCartLineToCartItem(raw: unknown): CartItem | null {
  const row = (raw ?? {}) as Record<string, unknown>;
  const name = String(row.name ?? '').trim();
  if (!name) return null;
  const menuOptions = Array.isArray(row.menuOptions)
    ? (row.menuOptions as CartItem['menuOptions'])
    : [];
  const itemTypeRaw = row.itemType;
  const itemType =
    itemTypeRaw === 'custom' || itemTypeRaw === 'package' ? itemTypeRaw : 'product';
  return {
    lineId: String(row.lineId ?? row.clientLineId ?? row.id ?? createLineId()),
    productId: String(row.productId ?? ''),
    variantId: row.variantId != null ? String(row.variantId) : null,
    name,
    sku: row.sku != null ? String(row.sku) : undefined,
    category: (row.category as CartItem['category']) ?? null,
    brandId: row.brandId != null ? String(row.brandId) : null,
    price: Number(row.price) || 0,
    quantity: Math.max(1, Number(row.quantity ?? row.qty) || 1),
    itemType,
    affectsStock: row.affectsStock !== false,
    unitOfMeasure: row.unitOfMeasure as CartItem['unitOfMeasure'],
    sellByWeight: Boolean(row.sellByWeight),
    menuOptions,
    lineNote: row.lineNote != null ? String(row.lineNote) : null,
    performedByUserId:
      row.performedByUserId != null ? String(row.performedByUserId) : null,
    soldByUserId: row.soldByUserId != null ? String(row.soldByUserId) : null,
    kitchenSent: Boolean(row.kitchenSent),
    kitchenTicketId: row.kitchenTicketId != null ? String(row.kitchenTicketId) : null,
  };
}

function mapDraftLineToCartItem(line: TableOrderDraftLine): CartItem {
  return {
    lineId: line.clientLineId || line.id,
    productId: line.productId ?? '',
    name: line.name,
    price: line.unitPrice,
    quantity: line.qty,
    itemType: 'product',
    affectsStock: true,
    category: null,
    menuOptions: Array.isArray(line.optionsJson)
      ? (line.optionsJson as CartItem['menuOptions'])
      : [],
    lineNote: line.unitNote,
    kitchenSent: false,
  };
}

/** Cuenta abierta + pendiente (misma regla que Cobrar en Mesas). */
export function buildTableCheckoutCart(payload: StaffTableDraftPayload): CartItem[] {
  const accountOpen = payload.account?.sessionStatus === 'open';
  const accountCart =
    accountOpen && Array.isArray(payload.account?.cartJson)
      ? payload.account.cartJson
          .map(mapStoredCartLineToCartItem)
          .filter((line): line is CartItem => line != null)
      : [];
  const draftCart = (payload.draft?.lines ?? []).map(mapDraftLineToCartItem);
  return [...accountCart, ...draftCart];
}
